/**
 * Permission utility functions for handling authorization checks
 */

/**
 * Checks if user has a specific permission
 * @param permission The permission to check
 * @param permissions User's permissions array
 * @returns boolean
 */
export const hasPermission = (permission: string, permissions: string[]): boolean => {
  return permissions.includes(permission);
};

/**
 * Checks if user has any of the required permissions
 * @param requiredPermissions Array of permissions to check
 * @param userPermissions User's permissions array
 * @returns boolean
 */
export const hasAnyPermission = (
  requiredPermissions: string[],
  userPermissions: string[]
): boolean => {
  if (requiredPermissions.length === 0) return true; // No permissions required
  return requiredPermissions.some(permission => 
    userPermissions.includes(permission)
  );
};

/**
 * Checks if user has all of the required permissions
 * @param requiredPermissions Array of permissions to check
 * @param userPermissions User's permissions array
 * @returns boolean
 */
export const hasAllPermissions = (
  requiredPermissions: string[],
  userPermissions: string[]
): boolean => {
  return requiredPermissions.every(permission => 
    userPermissions.includes(permission)
  );
};

/**
 * Permission groups for common route patterns
 */
export const PERMISSION_GROUPS = {
  // User Management
  USER_MANAGEMENT: [
    'users.create',
    'users.edit',
    'users.delete',
    'users.view_audit_logs',
    'users.view_file_reports',
    'users.view_credits',
    // The per-tab permissions below are enough on their own to open the
    // page - without them a role granted only "View Paid Users" would be
    // bounced to /not-authorized before it ever saw its tab.
    'users.view_paid',
    'users.view_free_trial',
    'users.view_inactive_plan',
    'users.view_sub_users',
    'users.view_insnap'
  ],
  
  // Role Management
  ROLE_MANAGEMENT: [
    'user_role.create',
    'user_role.edit',
    'user_role.delete'
  ],
  
  // Email Operations 
  EMAIL_OPERATIONS: [
    'bulk_email_verify.create',
    'bulk_email_finder.create',
    'bulk_email_verify.view_file_stats',
    'bulk_email_verify.download_uploaded_data',
    'bulk_email_finder.view_file_stats',
    'bulk_email_finder.download_uploaded_data',
    'verify_email.create',
    'verify_email.view_file_stats',
    'finder_email.create',
    'finder_email.view_file_stats',
    'api_history.view_file_stats',
    // Its tab is the only way in - it has no sidebar page of its own.
    'hubspot_integration.view'
  ],
  
  // System Configuration
  SYSTEM_CONFIG: [
    'email_pattern.create',
    'email_pattern.edit',
    'email_pattern.delete',
    'server_list.create',
    'server_list.edit',
    'server_list.delete',
    'server_ip.create',
    'server_ip.edit',
    'server_ip.delete'
  ],
  
  // Coupons
  COUPON_MANAGEMENT: [
    'coupon.create',
    'coupon.edit',
    'coupon.view_usage'
  ],

  // Plans & Billing
  BILLING_MANAGEMENT: [
    'billing_plan.create',
    'billing_plan.edit',
    'billing_plan.delete',
    'billing_plan.allocate',
    'billing_plan.stripe_sync'
  ]
};

/**
 * Gets the required permissions for a given route path
 * @param path The route path
 * @returns Array of required permissions
 */
export const getPermissionsForRoute = (path: string): string[] => {
  const routePermissions: Record<string, string[]> = {
    '/admin-overview': ['overview.view'],
    '/user-management': [...PERMISSION_GROUPS.USER_MANAGEMENT, ...PERMISSION_GROUPS.ROLE_MANAGEMENT],
    '/users-list': PERMISSION_GROUPS.USER_MANAGEMENT,
    '/users-roles': PERMISSION_GROUPS.ROLE_MANAGEMENT,
    '/email-settings': ['email_pattern.create', 'email_pattern.edit', 'email_pattern.delete', 'verification_settings.view', 'verification_settings.edit'],
    '/email-patterns': ['email_pattern.create', 'email_pattern.edit', 'email_pattern.delete'],
    '/netbox-management': ['server_list.create', 'server_list.edit', 'server_list.delete', 'server_ip.create', 'server_ip.edit', 'server_ip.delete'],
    '/server-ip-details': ['server_list.create', 'server_list.edit', 'server_list.delete'],
    '/ovh-fleet-status': ['server_list.create', 'server_list.edit', 'server_list.delete'],
    '/server-ips-list': ['server_list.create', 'server_list.edit', 'server_list.delete'],
    '/email-verifier-stats': ['server_list.create', 'server_list.edit', 'server_list.delete'],
    '/system-event-logs': ['server_list.create', 'server_list.edit', 'server_list.delete', 'server_list.view'],
    '/api-history': ['api_history.view_file_stats'],
    '/admin-audit-logs': ['users.view_audit_logs'],
    '/email-operations': PERMISSION_GROUPS.EMAIL_OPERATIONS,
    '/bulk-email-verify': ['bulk_email_verify.create'],
    '/bulk-email-finder': ['bulk_email_finder.create'],
    '/verify-email': ['verify_email.create'],
    '/email-finder': ['finder_email.create'],
    '/logs-history': ['log_history.view'],
    '/billing-management': [...PERMISSION_GROUPS.BILLING_MANAGEMENT, ...PERMISSION_GROUPS.COUPON_MANAGEMENT],
    '/profile': [] // No permissions required
  };

  return routePermissions[path] || [];
};

/**
 * role_ids that land in the admin panel after login. Everyone else lands in
 * the customer panel (/dashboard).
 */
export const ADMIN_ROLE_IDS = [1, 4, 9, 10];

export const isAdminRoleId = (roleId?: number | null): boolean =>
  roleId != null && ADMIN_ROLE_IDS.includes(roleId);

// Same order as the admin Sidebar (Sidebar.tsx ALL_SIDEBAR_LINKS) - the first
// entry the logged-in user has permission for is where they land after
// login, instead of a hardcoded page they may not even have access to.
const ADMIN_LANDING_LOOKUP_PATHS: string[] = [
  '/admin-overview',
  '/user-management',
  '/email-operations',
  '/email-settings',
  '/netbox-management',
  '/server-ip-details',
  '/ovh-fleet-status',
  '/system-event-logs',
  '/billing-management',
  '/logs-history',
];

// Sidebar looks up permissions for "Monitoring" via '/server-ip-details' but
// links to '/server-ip-details/stats' - mirror that split here too.
const ADMIN_LANDING_PATH_OVERRIDES: Record<string, string> = {
  '/server-ip-details': '/server-ip-details/stats',
};

export const getDefaultAdminPath = (permissions: string[]): string => {
  for (const lookupPath of ADMIN_LANDING_LOOKUP_PATHS) {
    if (hasAnyPermission(getPermissionsForRoute(lookupPath), permissions)) {
      return ADMIN_LANDING_PATH_OVERRIDES[lookupPath] || lookupPath;
    }
  }
  return '/not-authorized';
};

/**
 * Where a `?next=` on the sign-in page is allowed to send someone.
 *
 * The HubSpot Marketplace Install button is why this exists: our OAuth
 * callback only accepts a state that Connect to HubSpot created, so an
 * install started from HubSpot's side fails with "That connection link
 * expired". The button therefore points at our sign-in page, and that page
 * has to finish on Integrations rather than the dashboard.
 *
 * An allowlist, not a "starts with /" test: that test still passes `//evil.com`
 * and `/\evil.com`, which browsers read as protocol-relative URLs, and an open
 * redirect on a login page is a ready-made phishing link.
 */
const NEXT_PATH_ALLOWLIST = new Set<string>(['/integration']);

export const safeNextPath = (next: string | null | undefined): string | null =>
  next && NEXT_PATH_ALLOWLIST.has(next) ? next : null;

/**
 * Single source of truth for "where does this user land right after
 * login" - used by every login method (email/password, Google, LinkedIn,
 * Microsoft) so the redirect logic only lives in one place.
 *
 * `next` is ignored for admins: their landing page depends on which
 * permissions they hold, and a stray query parameter must not override it.
 */
export const getPostLoginPath = (
  authResponse: {
    role_id?: number | null;
    permissions?: string[];
  },
  next?: string | null,
): string => {
  if (isAdminRoleId(authResponse.role_id)) {
    return getDefaultAdminPath(authResponse.permissions || []);
  }
  return safeNextPath(next) || '/dashboard';
};